import { useEffect, useRef, useState } from "react";

/**
 * Reveal — wraps children with a fade+slide-up animation triggered when the element
 * enters the viewport. Lightweight (IntersectionObserver, no framer-motion).
 *
 * Props:
 *   delay   — number (ms), animation-delay before reveal kicks in
 *   y       — number (px), distance to slide up from (default 24)
 *   as      — html tag (default "div")
 *   once    — boolean, if true (default) animates only the first time
 *   className — extra classes for the wrapper
 *
 * Respects `prefers-reduced-motion`.
 */
export function Reveal({ children, delay = 0, y = 24, as: Tag = "div", once = true, className = "", ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(m.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    m.addEventListener?.("change", onChange);
    return () => m.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    if (!ref.current || reducedMotion) { setShown(true); return; }
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            if (once) io.unobserve(el);
          } else if (!once) {
            setShown(false);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [once, reducedMotion]);

  const style = {
    transition: "opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1)",
    transitionDelay: `${delay}ms`,
    opacity: shown ? 1 : 0,
    transform: shown ? "translate3d(0,0,0)" : `translate3d(0, ${y}px, 0)`,
    willChange: "opacity, transform",
  };

  return <Tag ref={ref} style={style} className={className} {...rest}>{children}</Tag>;
}

/**
 * Stagger — splits a list of children into staggered reveals (each child delayed by `step` ms).
 */
export function Stagger({ children, step = 80, baseDelay = 0, className = "" }) {
  const arr = Array.isArray(children) ? children : [children];
  return (
    <>
      {arr.map((c, i) => (
        <Reveal key={i} delay={baseDelay + i * step} className={className}>{c}</Reveal>
      ))}
    </>
  );
}
